import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Card, toast } from '@vwt/ui';
import { useApiMutation } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { formatDate } from '../../lib/format.js';
import { useTranslation } from '../../lib/i18n.js';
import { SectionTitle } from '../../components/common.js';
import { connectNotion, notionOutcomeToast, useNotionStatus } from '../documents/notion-import.js';

/** Profile card: the person's Notion connection for document imports (ADR-0054). Hidden when not configured. */
export function NotionConnection() {
  const { t } = useTranslation();
  const status = useNotionStatus();
  const [params, setParams] = useSearchParams();
  const [connecting, setConnecting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const disconnect = useApiMutation('disconnectNotion', { invalidate: ['getNotionConnection'] });

  useEffect(() => {
    const outcome = params.get('notion');
    if (!outcome) return;
    notionOutcomeToast(t, outcome);
    setParams(
      (current) => {
        current.delete('notion');
        return current;
      },
      { replace: true },
    );
  }, [params, setParams, t]);

  const view = status.data?.data;
  if (!view?.available) return null;
  const connection = view.connection;

  const connect = async () => {
    setConnecting(true);
    try {
      await connectNotion('/settings');
    } catch (error) {
      setConnecting(false);
      toastError(error);
    }
  };

  const remove = async () => {
    try {
      await disconnect.mutateAsync({});
      setConfirming(false);
      toast.success(t('settings.notion.disconnected'));
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <Card className="flex flex-col gap-3 p-5" data-testid="notion-connection">
      <SectionTitle>{t('settings.notion.title')}</SectionTitle>
      <p className="text-sm text-fg-muted">{t('settings.notion.description')}</p>
      {connection ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="min-w-0 flex-1">
            {connection.workspaceIcon && !connection.workspaceIcon.startsWith('http') ? <span aria-hidden>{connection.workspaceIcon} </span> : null}
            {t('settings.notion.connectedTo', { workspace: connection.workspaceName ?? connection.workspaceId, date: formatDate(connection.createdAt) })}
          </span>
          {confirming ? (
            <span className="flex items-center gap-2">
              <span className="text-xs text-fg-muted">{t('settings.notion.confirmDisconnect')}</span>
              <Button size="sm" variant="danger" loading={disconnect.isPending} onClick={() => void remove()}>
                {t('settings.notion.disconnect')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                {t('common.cancel')}
              </Button>
            </span>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
              {t('settings.notion.disconnect')}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-fg-subtle">{t('settings.notion.notConnected')}</span>
          <Button size="sm" variant="secondary" loading={connecting} onClick={() => void connect()}>
            {t('settings.notion.connect')}
          </Button>
        </div>
      )}
      <p className="text-xs text-fg-subtle">{t('settings.notion.revokeHint')}</p>
    </Card>
  );
}
